import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import type { BufferRange, PacketProvider, PacketProviderInfo, PacketProviderStreamInfo, Packet } from './demuxAndDecodeFrameProvider';
import type { Resolution } from '@mimir/common/transcoding/types';
import type { Fraction } from '@mimir/common/util/fractionType';
import { createDummyFrameProvider } from '@mimir/cutter/frameProvider/testSignalFrameProvider';
import { approximateFractionValue, divideFractions, invertFraction, multiplyFractions, subtractFractions } from '@mimir/common/util/integerMath';
import { encodeTestPacket, TestPacketHeaderAudio, TestPacketHeaderVideo } from './testPacket';
import { AudioFrameV2, VideoFrameV2, PreloadInfo } from '@mimir/cutter/frameProvider/frameProvider';
import { createAudioCache } from './audioCache';
import { NO_LOGGER } from '@mimir/common/util/logging';
import { BufferCache } from '@mimir/cutter/frameProvider/refCountedBuffer';

export interface TestSignalPacketProviderSettings {
  resolution: Resolution,
  frameRate: Fraction,
  sampleRate: number,
  gopSize: number,
  duration: Fraction,
  samplesPerPacket: number,
  bufferCache: BufferCache,
}

export async function createTestSignalPacketProvider(
  settings: TestSignalPacketProviderSettings,
  abortSignal: AbortSignal,
): Promise<PacketProvider> {
  return await createTestSignalPacketProviderInternal(settings, abortSignal);
}

// ---

async function createTestSignalPacketProviderInternal(
  settings: TestSignalPacketProviderSettings,
  abortSignal: AbortSignal,
): Promise<PacketProvider> {
  const {
    resolution,
    frameRate,
    gopSize,
    sampleRate,
    duration,
    samplesPerPacket,
    bufferCache,
  } = settings;

  let currentFrameNumber = 0;
  let frameProvider = createDummyFrameProvider(resolution, frameRate, sampleRate, bufferCache);
  const frameCount = Math.round(approximateFractionValue(multiplyFractions(duration, frameRate)));

  const firstFrame = await frameProvider.getFrame(0, null, abortSignal);

  const pendingPackets : Packet[] = [];

  const audioCache = createAudioCache(NO_LOGGER, bufferCache);

  // AAC packets are fixed size which means that they don't align perfectly
  // with the frame grid. We want to emulate that here so that we get to
  // test the code paths that merge together audio packets and return whole
  // frames of audio:
  const audioPacketDuration = { numerator: samplesPerPacket, denominator: sampleRate };

  let sampleOffset : number | null = null;
  return {
    async seek(logger: StructuredLogger, position: Fraction): Promise<void> {
      logger.log({ level: 'debug', message: 'TestSignalPacketProvider.seek()', position });
      // Since we emulate AAC packets the packets does not align perfectly with
      // the frame grid and because of this we need to seek one audio packet
      // further back than requested in order to make sure we get all the
      // samples that cover this frame.
      const positionAdjustedForAudio = subtractFractions(position, audioPacketDuration);
      const frameNumber = Math.floor(approximateFractionValue(multiplyFractions(positionAdjustedForAudio, frameRate)));
      currentFrameNumber = Math.max(Math.floor(frameNumber / gopSize) * gopSize, 0);
      logger.log({ level: 'debug', message: 'TestSignalPacketProvider.seek: ', frameNumber, currentFrameNumber });
      sampleOffset = null;
      audioCache.clear();
      pendingPackets.splice(0);

    },
    async poll(logger: StructuredLogger, abortSignal: AbortSignal): Promise<Packet | null> {
      logger.log({ level: 'debug', message: 'poll()', currentFrameNumber });
      if (pendingPackets.length === 0) {
        logger.log({ level: 'debug', message: 'poll: No pending packets.', currentFrameNumber });

        if (currentFrameNumber >= frameCount) {
          logger.log({ level: 'debug', message: 'poll(): Passed frame count.',  frameCount });
          return null;
        }

        const frame = await frameProvider.getFrame(currentFrameNumber, null, abortSignal);
        currentFrameNumber ++;

        let streamIndex = 0;
        if (frame.video) {
          pendingPackets.push(await makeVideoPacket(streamIndex, frame.frameNumber, frameRate, frame.video, gopSize));
          streamIndex ++;
        }

        if (frame.audio !== null) {
          const audioStreamIndex = streamIndex;
          streamIndex ++;

          audioCache.push(frame.audio);

          if (sampleOffset === null) {
            sampleOffset = frame.audio.sampleOffset;
          }

          // Make sure we align with the packet grid (this should only occur the first time after seek)
          const discard = (samplesPerPacket - (sampleOffset % samplesPerPacket)) % samplesPerPacket;
          if (discard > 0) {
            logger.log({ level: 'debug', message: 'poll(): discarding samples to achive alignment', discard });
            audioCache.remove(sampleOffset, sampleOffset + discard);
            sampleOffset += discard;
          }

          while (true) {
            const audioFrame = audioCache.get(sampleOffset, samplesPerPacket);
            if (audioFrame === null) {
              break;
            }
            sampleOffset += samplesPerPacket;
            logger.log({ level: 'debug', message: 'poll(): making audio packet', sampleCount: samplesPerPacket });
            pendingPackets.push(await makeAudioPacket(audioStreamIndex, audioFrame));
          }
        }
      }

      const [pendingPacket] = pendingPackets.splice(0, 1);
      if (pendingPacket !== undefined) {
        return pendingPacket;
      }

      if (pendingPackets.length === 0) {
        throw new Error('No content in TestSignalPacketProvider');
      }

      return pendingPacket;
    },
    bufferedRanges(): BufferRange[] {
      return [{
        start: { numerator: 0, denominator: 1 },
        end: duration,
        kind: 'demuxed',
      }];
    },
    info(): PacketProviderInfo {
      const streams : PacketProviderStreamInfo[] = [];
      if (firstFrame.video !== null) {
        streams.push({
          type: 'video',
          codec: 'test_video_codec',
          frameRate: firstFrame.frameRate,
          gopSize,
        });
      }
      if (firstFrame.audio !== null) {
        streams.push({
          type: 'audio',
          codec: 'test_audio_codec',
          sampleRate: firstFrame.audio.sampleRate,
          channelCount: firstFrame.audio.channelCount,
        });
      }
      return {
        duration,
        streams,
      };
    },
    async preload(_startPosition: Fraction, _preloadInfo: PreloadInfo): Promise<void> { }
  }
}

async function makeVideoPacket(
  streamIndex: number,
  frameNumber: number,
  frameRate: Fraction,
  video: VideoFrameV2,
  gopSize: number
): Promise<Packet> {
  const keyFrame = (frameNumber % gopSize) == 0;

  const header : TestPacketHeaderVideo = {
    type: 'video',
    keyFrame,
    frameRate,
    resolution: video.resolution,
    pixelFormat: video.pixelFormat,
    fieldOrder: video.fieldOrder,
  };

  const pts = divideFractions({ numerator: frameNumber, denominator: 1 }, frameRate);

  const payload = new Uint8Array(video.buffer.size());
  await video.buffer.copyTo(payload);
  video.buffer.close();

  return {
    type: 'video',
    streamIndex,
    codec: 'test_video_codec',
    keyFrame,
    pts,
    // TODO: Test B-frame?
    dts: pts,
    duration: invertFraction(frameRate),
    payload: encodeTestPacket({ header, payload }),
  };
}

async function makeAudioPacket(
  streamIndex: number,
  audioFrame: AudioFrameV2,
): Promise<Packet> {
  const header : TestPacketHeaderAudio = {
    type: 'audio',
    sampleCount: audioFrame.sampleCount,
    sampleRate: audioFrame.sampleRate,
    channelCount: audioFrame.channelCount,
    format: audioFrame.format,
  };

  const pts : Fraction = {
    numerator: audioFrame.sampleOffset,
    denominator: audioFrame.sampleRate
  };

  const payload = new Uint8Array(audioFrame.buffer.size());
  await audioFrame.buffer.copyTo(payload);

  return {
    type: 'audio',
    streamIndex,
    codec: 'test_audio_codec',
    pts,
    dts: pts,
    sampleRate: audioFrame.sampleRate,
    duration: {
      numerator: audioFrame.sampleCount,
      denominator: audioFrame.sampleRate
    },
    payload: encodeTestPacket({ header, payload }),
  };
}

